import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { ConVersion } from '../../../api/cliente';
import type { Cliente, EvaluacionPuerta } from '../../../api/ops/clientes';
import type { NombreIcono } from '../../../componentes/iconos';
import { Ayuda } from '../../../componentes/Ayuda';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { estadoDeError } from '../../../logica/estados';
import { Avatar } from '../../../marco/piezas';
import type { ContextoClientes } from '../lista/contexto';
import {
    AYUDA,
    BLOQUEADA,
    CANALES,
    MOTIVO_PUERTA,
    NIVEL_PUERTA,
    ORIGEN_FICHA,
    PERMISOS,
    PESTANAS,
    diaCorto,
    diaYHora,
    diasDesde,
    estaBloqueada,
    fechaCompleta,
    importe,
    inicialesDe,
    mesYAnio,
    telefonoLegible,
    type PestanaFicha,
} from '../lista/textos';
import { VentanaBloqueo } from './BloqueoLegal';
import { Datos, type GuardarDatos } from './Datos';
import { Entregas } from './Entregas';
import { Historial } from './Historial';
import { Notas } from './Notas';
import { Permisos } from './Permisos';

/**
 * Ficha de cliente (2.3) en RV Panel con su botonera Resumen · Datos · Historial · Permisos · Notas · Entregas. Resumen:
 * las cifras de `CustomerStats` sin gráfica ni «A vigilar» (G144), etiquetas, la fila «Puerta» con la evaluación vigente
 * (motivo de la lista cerrada, «Caduca el…», sin Verde: G146, G193), las alergias que guardó el propio cliente, solo lectura
 * con su fecha (G192), y el bloqueo por obligación legal (G196), que marca y quita quien atiende la protección de datos.
 */
export function Ficha({ c, id, pestanaInicial, nombreNegocio }: { c: ContextoClientes; id: string; pestanaInicial: PestanaFicha; nombreNegocio: string }) {
    const { ops, negocio } = c;
    const [ficha, setFicha] = useState<ConVersion<Cliente> | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [puerta, setPuerta] = useState<EvaluacionPuerta | null>(null);
    const [pestana, setPestana] = useState<PestanaFicha>(pestanaInicial);
    const [guardar, setGuardar] = useState<GuardarDatos | null>(null);
    const [bloqueo, setBloqueo] = useState<'poner' | 'quitar' | null>(null);

    useEffect(() => {
        let vivo = true;
        ops.getCustomer(negocio, id)
            .then((f) => {
                if (vivo) {
                    setFicha(f);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        ops.listDoorAssessments(negocio, id, { limit: 1 })
            .then((l) => {
                if (vivo) {
                    setPuerta(l.data[0] ?? null);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, intento]);

    // Datos sube su «Guardar» a la cabecera del panel (una función: con `() => g` para que React no la llame).
    const fijarGuardar = useCallback((g: GuardarDatos | null) => {
        setGuardar(() => g);
    }, []);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    const actualizar = useCallback(
        (f: ConVersion<Cliente>) => {
            setFicha(f);
            c.refrescarLista();
        },
        [c],
    );

    const cliente = ficha?.datos ?? null;
    // «Entregas» se ve siempre: mientras el módulo de Entregas no exista, su permiso no llega y la API responde 404.
    const pestanas = PESTANAS;
    const vinculada = cliente?.status === 'linked' || cliente?.account != null;
    const bloqueada = cliente !== null && estaBloqueada(cliente);
    const subtitulo =
        cliente === null
            ? undefined
            : vinculada
              ? `Cuenta Reservaria vinculada • cliente desde ${mesYAnio(cliente.created_at)}`
              : `Cliente de tu negocio, sin vincular • desde ${mesYAnio(cliente.created_at)}`;

    const cuerpo = (() => {
        if (error !== null && cliente === null) {
            return <EstadoError seccion="la ficha" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={c.movil} />;
        }
        if (cliente === null || ficha === null) {
            return <EstadoCargando seccion="la ficha" />;
        }
        switch (pestana) {
            case 'datos':
                return <Datos key={`${cliente.id}.${ficha.version ?? ''}`} c={c} ficha={ficha} onFicha={actualizar} onRecargar={recargar} onGuardar={fijarGuardar} />;
            case 'historial':
                return <Historial c={c} id={cliente.id} />;
            case 'permisos':
                return <Permisos c={c} cliente={cliente} />;
            case 'notas':
                return <Notas c={c} id={cliente.id} />;
            case 'entregas':
                return <Entregas c={c} cliente={cliente} />;
            default:
                return (
                    <Resumen
                        c={c}
                        cliente={cliente}
                        puerta={puerta}
                        onHistorial={() => {
                            setPestana('historial');
                        }}
                        onBloqueo={setBloqueo}
                    />
                );
        }
    })();

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={c.cerrarFicha}
                movil={c.movil}
                tamano="ficha"
                eyebrow="Cliente"
                titulo={cliente?.display_name ?? ''}
                subtitulo={subtitulo}
                chip={bloqueada ? BLOQUEADA : undefined}
                chipEstado="problem"
                accion={pestana === 'datos' && guardar !== null ? 'Guardar' : ''}
                onAccion={() => {
                    guardar?.();
                }}
            >
                <div className="rv-cl-ficha">
                    <div className="rv-cl-botonera" role="tablist" aria-label="Apartados de la ficha">
                        {pestanas.map(([clave, texto, icono]) => (
                            <button
                                key={clave}
                                type="button"
                                role="tab"
                                aria-selected={pestana === clave}
                                className="rv-cl-botonera__b"
                                onClick={() => {
                                    setPestana(clave);
                                    setGuardar(null);
                                }}
                            >
                                <Icono nombre={icono} />
                                <span>{texto}</span>
                            </button>
                        ))}
                    </div>
                    <div className="rv-cl-ficha__contenido">
                        <div className="rv-cl-ficha__cab">
                            {cliente !== null && <Avatar texto={inicialesDe(cliente.display_name)} tamano={52} fondo="var(--rv-ink)" tinta="#fff" letra={16} />}
                            <h3 className="rv-cl-ceja">{PESTANAS.find(([p]) => p === pestana)?.[1] ?? ''}</h3>
                            <span style={{ flex: 1 }} />
                            <Ayuda tarjetas={AYUDA[pestana]} />
                        </div>
                        {cuerpo}
                    </div>
                </div>
            </PanelLateral>
            {cliente !== null && (
                <VentanaBloqueo
                    c={c}
                    cliente={cliente}
                    modo={bloqueo}
                    nombreNegocio={nombreNegocio}
                    onCerrar={() => {
                        setBloqueo(null);
                    }}
                    onHecho={() => {
                        setBloqueo(null);
                        recargar();
                        c.refrescarLista();
                    }}
                />
            )}
        </>
    );
}

function Linea({ etiqueta, icono, children }: { etiqueta: string; icono?: NombreIcono; children: ReactNode }) {
    return (
        <div className="rv-cl-dato">
            <span>
                {icono !== undefined && <Icono nombre={icono} />}
                {etiqueta}
            </span>
            <strong>{children}</strong>
        </div>
    );
}

function Cifra({ titulo, valor, unidad, sub, oscura = false, onClick }: { titulo: string; valor: string; unidad?: string; sub: string; oscura?: boolean; onClick?: () => void }) {
    const dentro = (
        <>
            <span className="rv-cl-cifra__titulo">
                {titulo}
                {onClick !== undefined && <Icono nombre="chevron-right" />}
            </span>
            <span className="rv-cl-cifra__valor">
                {valor}
                {unidad !== undefined && unidad !== '' && <small>{unidad}</small>}
            </span>
            <span className="rv-cl-cifra__sub">{sub}</span>
        </>
    );
    return onClick === undefined ? (
        <div className={`rv-cl-cifra${oscura ? ' rv-cl-cifra--oscura' : ''}`}>{dentro}</div>
    ) : (
        <button type="button" className={`rv-cl-cifra${oscura ? ' rv-cl-cifra--oscura' : ''}`} aria-label={`${titulo}: ${valor}. Ver el historial`} onClick={onClick}>
            {dentro}
        </button>
    );
}

function Resumen({ c, cliente, puerta, onHistorial, onBloqueo }: { c: ContextoClientes; cliente: Cliente; puerta: EvaluacionPuerta | null; onHistorial: () => void; onBloqueo: (m: 'poner' | 'quitar') => void }) {
    const s = cliente.stats ?? {};
    const visitas = s.visits_count ?? 0;
    const faltas = s.no_shows_count ?? 0;
    const gasto = importe(s.total_spent);
    const ultima = s.last_visit_at ?? null;
    const diaUltima = ultima === null ? '' : diaCorto(ultima);
    const [numUltima = '', mesUltima = ''] = diaUltima === 'Hoy' || diaUltima === 'Ayer' ? [diaUltima] : diaUltima.split(' ');
    const dias = ultima === null ? 0 : diasDesde(ultima);
    const etiquetas = (cliente.tag_ids ?? []).map((t) => c.etiquetas.find((x) => x.id === t)?.name).filter((n): n is string => n !== undefined);
    const telefono = telefonoLegible(cliente.account?.phone ?? cliente.local.phone);
    const email = cliente.account?.email ?? cliente.local.email ?? '';
    const vinculada = cliente.status === 'linked' || cliente.account != null;
    const puedeBloquear = c.permisos.puede(PERMISOS.rgpd);

    return (
        <div className="rv-cl-seccion">
            <div className="rv-cl-cifras">
                <Cifra titulo="Visitas" valor={String(visitas)} sub={visitas > 0 && s.first_visit_at != null ? `Desde ${mesYAnio(s.first_visit_at)}` : 'Todavía ninguna'} oscura onClick={onHistorial} />
                <Cifra titulo="Gasto acumulado" valor={gasto.cifra} unidad={gasto.moneda} sub="Pagos menos devoluciones" />
                <Cifra titulo="Sin presentarse" valor={String(faltas)} unidad={faltas === 0 ? '' : faltas === 1 ? 'vez' : 'veces'} sub={faltas > 0 ? `De ${String(visitas + faltas)} reservas y citas` : 'Nunca'} />
                {ultima === null ? (
                    <Cifra titulo="Última visita" valor="—" sub="Todavía no ha venido" />
                ) : (
                    <Cifra titulo="Última visita" valor={numUltima} unidad={mesUltima} sub={dias < 2 ? diaUltima : `Hace ${String(dias)} días`} />
                )}
            </div>

            <div className="rv-cl-dato">
                <span>Etiquetas</span>
                <span className="rv-cl-chips rv-cl-chips--derecha">
                    {etiquetas.length === 0 ? (
                        <strong>Sin etiquetas</strong>
                    ) : (
                        etiquetas.map((n) => (
                            <Chip key={n} estado="neutral">
                                {n}
                            </Chip>
                        ))
                    )}
                </span>
            </div>

            {puerta !== null && (
                <div className="rv-cl-dato rv-cl-dato--alto">
                    <span>
                        <Icono nombre="door-open" />
                        Puerta
                    </span>
                    <span className="rv-cl-puerta">
                        <Chip estado={NIVEL_PUERTA[puerta.level][1]}>{NIVEL_PUERTA[puerta.level][0]}</Chip>
                        <strong>{MOTIVO_PUERTA[puerta.reason_code]}</strong>
                        {puerta.reason_note != null && puerta.reason_note !== '' && <span>{puerta.reason_note}</span>}
                        <small>{`Última evaluación • ${diaYHora(puerta.created_at)} • Caduca el ${fechaCompleta(puerta.expires_at)}`}</small>
                    </span>
                </div>
            )}

            {cliente.health_info != null && (
                <div className="rv-cl-dato rv-cl-dato--alto">
                    <span>Alergias o intolerancias</span>
                    <span className="rv-cl-puerta">
                        <strong>{cliente.health_info.text}</strong>
                        <small>{`Lo guardó el cliente el ${fechaCompleta(cliente.health_info.consented_at)}. Solo él puede quitarlo, en «Mis datos».`}</small>
                    </span>
                </div>
            )}

            <Linea etiqueta="Cuenta Reservaria">
                <span className="rv-cl-cuenta">
                    <Icono nombre={vinculada ? 'circle-check' : 'unlink'} />
                    {vinculada ? 'Vinculada' : 'Sin vincular'}
                </span>
            </Linea>
            <Linea etiqueta="Canal preferido">
                {cliente.preferred_channel == null ? (
                    'Sin elegir'
                ) : (
                    <span className="rv-cl-cuenta">
                        <Icono nombre={CANALES[cliente.preferred_channel].icono} />
                        {CANALES[cliente.preferred_channel].nombre}
                    </span>
                )}
            </Linea>
            <Linea etiqueta="Teléfono">{telefono === '' ? '—' : telefono}</Linea>
            <Linea etiqueta="Email">{email === '' ? '—' : email}</Linea>
            {cliente.local.instagram_username != null && (
                <Linea etiqueta="Instagram">
                    <span className="rv-cl-cuenta">
                        <Icono nombre="instagram" />
                        {`@${cliente.local.instagram_username}`}
                    </span>
                </Linea>
            )}
            {cliente.source !== undefined && <Linea etiqueta="Llegó por">{ORIGEN_FICHA[cliente.source]}</Linea>}
            <Linea etiqueta="Cliente desde">{mesYAnio(cliente.created_at)}</Linea>
            {cliente.birth_date != null && (
                <div className="rv-cl-dato rv-cl-dato--alto">
                    <span>Fecha de nacimiento</span>
                    <span className="rv-cl-puerta">
                        <strong>{fechaCompleta(cliente.birth_date)}</strong>
                        {cliente.birth_date_consented_at != null && <small>{`Aceptó guardarla para felicitarle el ${fechaCompleta(cliente.birth_date_consented_at)}`}</small>}
                    </span>
                </div>
            )}

            {cliente.legal_hold != null ? (
                <div className="rv-cl-caja">
                    <span className="rv-cl-caja__titulo">
                        <Icono nombre="lock" />
                        {BLOQUEADA}
                    </span>
                    <span>{cliente.legal_hold.reason}</span>
                    <span className="rv-cl-nota">{`Desde el ${fechaCompleta(cliente.legal_hold.set_at)}. Si pide borrar sus datos, la ficha no se anonimiza: queda bloqueada, sin uso y solo a disposición de jueces y autoridades.`}</span>
                    {puedeBloquear && (
                        <Boton
                            tipo="secundario"
                            icono="lock-open"
                            movil={c.movil}
                            onClick={() => {
                                onBloqueo('quitar');
                            }}
                        >
                            Quitar el bloqueo
                        </Boton>
                    )}
                </div>
            ) : (
                puedeBloquear &&
                cliente.status !== 'anonymized' && (
                    <div className="rv-cl-fila-boton">
                        <Boton
                            tipo="secundario"
                            icono="lock"
                            movil={c.movil}
                            onClick={() => {
                                onBloqueo('poner');
                            }}
                        >
                            Bloquear por obligación legal o reclamación
                        </Boton>
                    </div>
                )
            )}
        </div>
    );
}
